import { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { notionAPI, projectAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  FileText, Search, Database, ArrowRight, Check, Loader2,
  Link2, Unlink, ChevronRight, Download, FolderKanban, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { StellarSelect } from '../components/StellarFormControls';
import { Badge } from '../components/ui/badge';
import { formatDateLong } from '../lib/dateFormat';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';

const STELLAR_FIELDS = [
  { value: 'title', label: 'Task Title' },
  { value: 'description', label: 'Description' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'due_date', label: 'Due Date' },
  { value: 'labels', label: 'Labels (multi-select)' },
  { value: '__skip__', label: 'Skip (do not import)' },
];

export default function NotionImportPage() {
  const { currentWorkspace } = useWorkspace();
  const [connected, setConnected] = useState(false);
  const [notionUser, setNotionUser] = useState(null);
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedDb, setSelectedDb] = useState(null);
  const [dbSchema, setDbSchema] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [projects, setProjects] = useState([]);
  const [targetProject, setTargetProject] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importingAll, setImportingAll] = useState(false);
  const [step, setStep] = useState(1); // 1=connect, 2=browse, 3=map, 4=result
  const wsId = currentWorkspace?.id;

  // Check connection status
  useEffect(() => {
    notionAPI.status().then(res => {
      setConnected(res.data.connected);
      if (res.data.connected) {
        setNotionUser(res.data.notion_user);
        setStep(2);
      }
    }).catch(() => {});
  }, []);

  // Load projects for mapping
  useEffect(() => {
    if (!wsId) return;
    projectAPI.list(wsId).then(res => setProjects(res.data || [])).catch(() => {});
  }, [wsId]);

  const connect = async () => {
    if (!token.trim()) { toast.error('Enter your Notion integration token'); return; }
    setConnecting(true);
    try {
      const res = await notionAPI.connect(token.trim());
      setConnected(true);
      setNotionUser(res.data);
      setStep(2);
      toast.success('Notion connected');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to connect'); }
    setConnecting(false);
  };

  const disconnect = async () => {
    try {
      await notionAPI.disconnect();
      setConnected(false);
      setNotionUser(null);
      setStep(1);
      toast.success('Disconnected');
    } catch {}
  };

  const search = async (q) => {
    setSearching(true);
    try {
      const res = await notionAPI.search(q, 'database');
      setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
    setSearching(false);
  };

  useEffect(() => {
    if (connected && step === 2) search('');
  }, [connected, step]);

  const selectDatabase = async (db) => {
    setSelectedDb(db);
    try {
      const res = await notionAPI.databaseSchema(db.id);
      setDbSchema(res.data);
      // Auto-map obvious fields
      const autoMap = {};
      for (const [name, prop] of Object.entries(res.data.properties || {})) {
        const lower = name.toLowerCase();
        if (prop.type === 'title') autoMap[name] = 'title';
        else if (lower.includes('status') || prop.type === 'status') autoMap[name] = 'status';
        else if (lower.includes('prior')) autoMap[name] = 'priority';
        else if (lower.includes('due') || lower.includes('date') || lower.includes('deadline')) autoMap[name] = 'due_date';
        else if (lower.includes('desc') || lower.includes('note')) autoMap[name] = 'description';
        else if (prop.type === 'multi_select' && (lower.includes('tag') || lower.includes('label'))) autoMap[name] = 'labels';
      }
      setFieldMapping(autoMap);
      setStep(3);
    } catch (err) { toast.error('Failed to load database schema'); }
  };

  const doImport = async () => {
    if (!wsId || !selectedDb) return;
    setImporting(true);
    try {
      const res = await notionAPI.importData(wsId, {
        database_id: selectedDb.id,
        field_mapping: fieldMapping,
        project_id: targetProject,
      });
      setImportResult(res.data);
      setStep(4);
      toast.success(`Imported ${res.data.imported} tasks`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8" data-testid="notion-import-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-display font-bold">Notion Import</h1>
          <p className="text-[12px] text-muted-foreground/50 mt-1">Import databases from Notion into Stellar CRM as tasks</p>
        </div>
        {connected && (
          <Button variant="outline" size="sm" className="text-[11px] h-7 border-border/30" onClick={disconnect}>
            <Unlink className="h-3 w-3 mr-1.5" /> Disconnect
          </Button>
        )}
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-6">
        {['Connect', 'Browse', 'Map Fields', 'Done'].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium ${step > i + 1 ? 'bg-gold/20 text-gold' : step === i + 1 ? 'bg-gold/10 text-gold border border-gold/30' : 'bg-foreground/[0.03] text-muted-foreground/30'}`}>
              {step > i + 1 ? <Check className="h-3 w-3" /> : i + 1}
            </div>
            <span className={`text-[11px] ${step === i + 1 ? 'text-foreground' : 'text-muted-foreground/30'}`}>{label}</span>
            {i < 3 && <ChevronRight className="h-3 w-3 text-muted-foreground/15" />}
          </div>
        ))}
      </div>

      {/* Step 1: Connect */}
      {step === 1 && (
        <Card className="bg-card/60 backdrop-blur border-border/30">
          <CardHeader>
            <CardTitle className="text-sm">Connect to Notion</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-foreground/[0.02] border border-border/20 p-4 space-y-2">
              <p className="text-[12px] text-muted-foreground/60 leading-relaxed">
                1. Go to <a href="https://www.notion.so/my-integrations" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">notion.so/my-integrations</a>
              </p>
              <p className="text-[12px] text-muted-foreground/60 leading-relaxed">
                2. Click "New integration" and give it a name (e.g., "Stellar CRM")
              </p>
              <p className="text-[12px] text-muted-foreground/60 leading-relaxed">
                3. Copy the "Internal Integration Secret" token below
              </p>
              <p className="text-[12px] text-muted-foreground/60 leading-relaxed">
                4. In Notion, share the databases you want to import with your integration
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                value={token}
                onChange={e => setToken(e.target.value)}
                placeholder="ntn_xxxxxxxxxxxx..."
                type="password"
                className="flex-1 h-9 text-[12px] border-border/20"
                data-testid="notion-token-input"
              />
              <Button onClick={connect} disabled={connecting} className="h-9 bg-gold/10 hover:bg-gold/20 text-gold border-0 text-[12px]" data-testid="notion-connect-btn">
                {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Link2 className="h-3.5 w-3.5 mr-1.5" />}
                Connect
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Browse databases */}
      {step === 2 && (
        <Card className="bg-card/60 backdrop-blur border-border/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Select a Notion Database</CardTitle>
              <Button size="sm" className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0 gap-1.5" onClick={async () => {
                if (!wsId) return;
                setImportingAll(true);
                try {
                  const res = await notionAPI.importAll(wsId);
                  setImportResult({ ...res.data, isAll: true });
                  setStep(4);
                  toast.success(`Imported ${res.data.imported} tasks from ${res.data.databases} databases`);
                } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
                setImportingAll(false);
              }} disabled={importingAll} data-testid="import-all-btn">
                {importingAll ? <><Loader2 className="h-3 w-3 animate-spin" /> Importing all...</> : <><Download className="h-3 w-3" /> Import Everything</>}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground/40 mt-1">Or select a single database below to import individually</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 h-[34px] px-2.5 rounded-md border border-border/20 focus-within:border-gold/30">
                <Search className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') search(searchQuery); }}
                  placeholder="Search databases..."
                  className="bg-transparent border-none outline-none text-[12px] flex-1 placeholder:text-muted-foreground/25"
                  data-testid="notion-search-input"
                />
              </div>
              <Button variant="outline" size="sm" className="h-[34px] text-[11px] border-border/20" onClick={() => search(searchQuery)}>Search</Button>
            </div>
            <ScrollArea className="max-h-[400px]">
              {searching ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/30" />
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="h-8 w-8 text-muted-foreground/10 mx-auto mb-2" />
                  <p className="text-[12px] text-muted-foreground/30">No databases found. Make sure you've shared them with your integration.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {searchResults.map(r => (
                    <button
                      key={r.id}
                      onClick={() => selectDatabase(r)}
                      className="flex items-center gap-3 w-full p-3 rounded-lg border border-border/20 hover:border-gold/20 hover:bg-foreground/[0.02] text-left transition-colors"
                      data-testid={`notion-db-${r.id}`}
                    >
                      <Database className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium truncate">{r.title}</p>
                        <p className="text-[10px] text-muted-foreground/30">{r.type} &middot; Last edited {formatDateLong(r.last_edited)}</p>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/20" />
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Map fields */}
      {step === 3 && dbSchema && (
        <Card className="bg-card/60 backdrop-blur border-border/30">
          <CardHeader>
            <CardTitle className="text-sm">Map Fields: {selectedDb?.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-[11px] text-muted-foreground/40">Map Notion columns to Stellar CRM task fields. Auto-mapped fields are pre-selected.</p>

            {/* Target project */}
            <div>
              <label className="text-[11px] text-muted-foreground/40 font-medium mb-1 block">Import into Project</label>
              <StellarSelect
                value={targetProject || '__none__'}
                onValueChange={(v) => setTargetProject(v === '__none__' ? '' : v)}
                options={[{ value: '__none__', label: 'No project (standalone tasks)' }, ...projects.map(p => ({ value: p.id, label: p.name }))]}
                size="sm"
                testId="import-project-select"
              />
            </div>

            {/* Field mapping table */}
            <div className="border border-border/20 rounded-lg overflow-hidden">
              <div className="flex items-center gap-4 px-4 py-2 bg-foreground/[0.02] border-b border-border/20">
                <span className="flex-1 text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider">Notion Column</span>
                <span className="text-[10px] text-muted-foreground/30 w-20 text-center">Type</span>
                <span className="flex-1 text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider">Stellar Field</span>
              </div>
              <ScrollArea className="max-h-[300px]">
                {Object.entries(dbSchema.properties || {}).map(([name, prop]) => (
                  <div key={name} className="flex items-center gap-4 px-4 py-2.5 border-b border-border/10 last:border-b-0" data-testid={`field-map-${name}`}>
                    <span className="flex-1 text-[12px] truncate">{name}</span>
                    <Badge variant="outline" className="text-[9px] w-20 justify-center border-border/20">{prop.type}</Badge>
                    <StellarSelect
                      value={fieldMapping[name] || '__skip__'}
                      onValueChange={(v) => setFieldMapping(prev => ({ ...prev, [name]: v === '__skip__' ? '' : v }))}
                      options={STELLAR_FIELDS.map(f => ({ value: f.value, label: f.label }))}
                      size="sm"
                      className="flex-1 h-7"
                    />
                  </div>
                ))}
              </ScrollArea>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" className="h-8 text-[12px] border-border/30" onClick={() => setStep(2)}>Back</Button>
              <Button size="sm" className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={doImport} disabled={importing} data-testid="notion-import-btn">
                {importing ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Importing...</> : <><Download className="h-3.5 w-3.5 mr-1.5" /> Import Tasks</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Results */}
      {step === 4 && importResult && (
        <Card className="bg-card/60 backdrop-blur border-border/30">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" /> Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-foreground/[0.02]">
                <p className="text-2xl font-display font-bold text-gold">{importResult.imported}</p>
                <p className="text-[11px] text-muted-foreground/40">Tasks Imported</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-foreground/[0.02]">
                <p className="text-2xl font-display font-bold">{importResult.total}</p>
                <p className="text-[11px] text-muted-foreground/40">Total Rows</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-foreground/[0.02]">
                <p className="text-2xl font-display font-bold text-red-500/60">{importResult.errors}</p>
                <p className="text-[11px] text-muted-foreground/40">Errors</p>
              </div>
            </div>
            {importResult.errors > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/[0.04] border border-red-500/20">
                <AlertTriangle className="h-4 w-4 text-red-500/60 mt-0.5 shrink-0" />
                <p className="text-[12px] text-red-400/80">{importResult.errors} rows could not be imported. Check task data for issues.</p>
              </div>
            )}
            <p className="text-[12px] text-muted-foreground/40">Imported tasks are tagged with "notion-import" label for easy filtering.</p>
            {importResult.projects_created && importResult.projects_created.length > 0 && (
              <div className="mt-2">
                <p className="text-[11px] text-muted-foreground/50 font-medium mb-1">Projects created:</p>
                {importResult.projects_created.map((p, i) => (
                  <p key={i} className="text-[12px] text-foreground/70 pl-2">- {p}</p>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-8 text-[12px] border-border/30" onClick={() => { setStep(2); setImportResult(null); }}>Import Another</Button>
              <Button size="sm" className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={() => window.location.href = '/tasks/list'}>
                <FolderKanban className="h-3.5 w-3.5 mr-1.5" /> View Tasks
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
